import { DateTime } from 'luxon';
import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AlertasDefault from 'Screen/Alertas';
import { ArquivosAdicionar } from 'Screen/Arquivos';
import AvaliacoesDesempenhoDefault from 'Screen/AvaliacoesDesempenho';
import AvaliacoesExperienciasDefault from 'Screen/AvaliacoesExperiencias';
import { BancosAdicionar, BancosEditar } from 'Screen/Bancos';
import CalculadoraDefault from 'Screen/Calculadora';
import CategoriasDeCurriculoDefault, {
  CategoriasDeCurriculoAdicionar,
  CategoriasDeCurriculoEditar,
} from 'Screen/CategoriasDeCurriculo';
import ComunicadosDefault, {
  ComunicadosAdicionar,
  ComunicadosEditar,
  ComunicadosTodos,
} from 'Screen/Comunicados';
import CurriculosDefault, {
  CurriculosAdicionar,
  CurriculosEditar,
} from 'Screen/Curriculos';
import DepartamentosDefault, {
  DepartamentosAdicionar,
  DepartamentosEditar,
} from 'Screen/Departamentos';
import EmpresasDefault, {
  EmpresasAdicionar,
  EmpresasEditar,
} from 'Screen/Empresas';
import {
  EmprestimosEpiAssinar,
  EmprestimosEpiHistorico,
} from 'Screen/EmprestimosEpi';
import {
  EmprestimosUniformeAssinar,
  EmprestimosUniformeHistorico,
} from 'Screen/EmprestimosUniforme';
import EpisDefault, { EpisAdicionar, EpisEditar } from 'Screen/Epis';
import FechamentosDeFolhaDefault, {
  FechamentosDeFolhaAdicionar,
  FechamentosDeFolhaAssinar,
  FechamentosDeFolhaEditar,
  FechamentosDeFolhaHistorico,
  FechamentosDeFolhaRelatorios,
} from 'Screen/FechamentosDeFolha';
import FuncoesDefault, {
  FuncoesAdicionar,
  FuncoesEditar,
} from 'Screen/Funcoes';
import HoleritesDefault, {
  HoleritesAdicionar,
  HoleritesAssinar,
  HoleritesEditar,
  HoleritesHistorico,
} from 'Screen/Holerites';
import IndisponibilidadesDefault, {
  IndisponibilidadesAdicionar,
  IndisponibilidadesAdicionarAdmin,
  IndisponibilidadesAguardando,
  IndisponibilidadesEditarAdmin,
} from 'Screen/Indisponibilidades';
import InicioDefault from 'Screen/Inicio';
import Menu from 'Screen/Menu';
import RelogiosDePontoDefault, {
  RelogiosDePontoAdicionar,
  RelogiosDePontoAssinar,
  RelogiosDePontoEditar,
  RelogiosDePontoHistorico,
} from 'Screen/RelogiosDePonto';
import TreinamentosDefault, {
  TreinamentosAdicionar,
  TreinamentosEditar,
  TreinamentosVisualizar,
} from 'Screen/Treinamentos';
import UniformesDefault, {
  UniformesAdicionar,
  UniformesEditar,
} from 'Screen/Uniformes';
import UsuariosDefault, {
  UsuariosAdicionar,
  UsuariosAlterarMinhaSenha,
  UsuariosAlterarSenha,
  UsuariosEditar,
  UsuariosLogin,
} from 'Screen/Usuarios';
import {
  VinculosEmpregaticiosAdicionar,
  VinculosEmpregaticiosEditar,
} from 'Screen/VinculosEmpregaticios';
import { useAppSelector } from 'Store';

interface Props {
  children: JSX.Element;
}

const Privado = ({ children }: Props): JSX.Element => {
  const localizacao = useLocation();
  const token = useAppSelector((estado) => estado.autenticacao.token);

  let permitido = false;

  if (token) {
    permitido = true;

    const dados = JSON.parse(atob(token.split('.')[1]));
    const expiracao = DateTime.fromSeconds(dados.exp);

    if (expiracao > DateTime.now()) permitido = true;
  }

  if (!permitido) {
    return <Navigate to="/login" state={{ from: localizacao }} replace />;
  }

  return children;
};

const Rota = (): React.ReactElement => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Privado>
            <UsuariosDefault />
          </Privado>
        }
      />
      <Route path="/login" element={<UsuariosLogin />} />
      <Route
        path="/inicio"
        element={
          <Privado>
            <InicioDefault />
          </Privado>
        }
      />
      <Route
        path="/empresas"
        element={
          <Privado>
            <EmpresasDefault />
          </Privado>
        }
      />
      <Route
        path="/empresas/adicionar"
        element={
          <Privado>
            <EmpresasAdicionar />
          </Privado>
        }
      />
      <Route
        path="/empresas/editar/:id"
        element={
          <Privado>
            <EmpresasEditar />
          </Privado>
        }
      />
      <Route
        path="/bancos/adicionar/:entidade/:id"
        element={
          <Privado>
            <BancosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/bancos/editar/:id"
        element={
          <Privado>
            <BancosEditar />
          </Privado>
        }
      />
      <Route
        path="/arquivos/adicionar/:entidade/:id"
        element={
          <Privado>
            <ArquivosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/departamentos"
        element={
          <Privado>
            <DepartamentosDefault />
          </Privado>
        }
      />
      <Route
        path="/departamentos/adicionar"
        element={
          <Privado>
            <DepartamentosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/departamentos/editar/:id"
        element={
          <Privado>
            <DepartamentosEditar />
          </Privado>
        }
      />
      <Route
        path="/funcoes"
        element={
          <Privado>
            <FuncoesDefault />
          </Privado>
        }
      />
      <Route
        path="/funcoes/adicionar"
        element={
          <Privado>
            <FuncoesAdicionar />
          </Privado>
        }
      />
      <Route
        path="/funcoes/editar/:id"
        element={
          <Privado>
            <FuncoesEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios"
        element={
          <Privado>
            <UsuariosDefault />
          </Privado>
        }
      />
      <Route
        path="/usuarios/adicionar"
        element={
          <Privado>
            <UsuariosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:id"
        element={
          <Privado>
            <UsuariosEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/alterar-senha/:id"
        element={
          <Privado>
            <UsuariosAlterarSenha />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:id/vinculos/adicionar"
        element={
          <Privado>
            <VinculosEmpregaticiosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/vinculos/editar/:id"
        element={
          <Privado>
            <VinculosEmpregaticiosEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/relogios-de-ponto/adicionar"
        element={
          <Privado>
            <RelogiosDePontoAdicionar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/relogios-de-ponto/editar/:id"
        element={
          <Privado>
            <RelogiosDePontoEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/fechamentos-de-folha/adicionar"
        element={
          <Privado>
            <FechamentosDeFolhaAdicionar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/fechamentos-de-folha/editar/:id"
        element={
          <Privado>
            <FechamentosDeFolhaEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/holerites/adicionar"
        element={
          <Privado>
            <HoleritesAdicionar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/holerites/editar/:id"
        element={
          <Privado>
            <HoleritesEditar />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/indisponibilidades/adicionar"
        element={
          <Privado>
            <IndisponibilidadesAdicionarAdmin />
          </Privado>
        }
      />
      <Route
        path="/usuarios/editar/:usuarioId/indisponibilidades/editar/:id"
        element={
          <Privado>
            <IndisponibilidadesEditarAdmin />
          </Privado>
        }
      />
      <Route
        path="/curriculos"
        element={
          <Privado>
            <CurriculosDefault />
          </Privado>
        }
      />
      <Route
        path="/curriculos/adicionar"
        element={
          <Privado>
            <CurriculosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/curriculos/editar/:id"
        element={
          <Privado>
            <CurriculosEditar />
          </Privado>
        }
      />
      <Route
        path="/comunicados"
        element={
          <Privado>
            <ComunicadosDefault />
          </Privado>
        }
      />
      <Route
        path="/comunicados/adicionar"
        element={
          <Privado>
            <ComunicadosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/comunicados/editar/:id"
        element={
          <Privado>
            <ComunicadosEditar />
          </Privado>
        }
      />
      <Route
        path="/comunicados/todos"
        element={
          <Privado>
            <ComunicadosTodos />
          </Privado>
        }
      />
      <Route
        path="/indisponibilidades"
        element={
          <Privado>
            <IndisponibilidadesDefault />
          </Privado>
        }
      />
      <Route
        path="/indisponibilidades/adicionar"
        element={
          <Privado>
            <IndisponibilidadesAdicionar />
          </Privado>
        }
      />
      <Route
        path="/indisponibilidades/aguardando"
        element={
          <Privado>
            <IndisponibilidadesAguardando />
          </Privado>
        }
      />
      <Route
        path="/treinamentos"
        element={
          <Privado>
            <TreinamentosDefault />
          </Privado>
        }
      />
      <Route
        path="/treinamentos/adicionar"
        element={
          <Privado>
            <TreinamentosAdicionar />
          </Privado>
        }
      />
      <Route
        path="/treinamentos/editar/:id"
        element={
          <Privado>
            <TreinamentosEditar />
          </Privado>
        }
      />
      <Route
        path="/treinamentos/visualizar/:id"
        element={
          <Privado>
            <TreinamentosVisualizar />
          </Privado>
        }
      />
      <Route
        path="/alterar-senha"
        element={
          <Privado>
            <UsuariosAlterarMinhaSenha />
          </Privado>
        }
      />
      <Route
        path="/calculadora"
        element={
          <Privado>
            <CalculadoraDefault />
          </Privado>
        }
      />
      <Route
        path="/alertas"
        element={
          <Privado>
            <AlertasDefault />
          </Privado>
        }
      />
      <Route
        path="/categorias-de-curriculo"
        element={
          <Privado>
            <CategoriasDeCurriculoDefault />
          </Privado>
        }
      />
      <Route
        path="/categorias-de-curriculo/adicionar"
        element={
          <Privado>
            <CategoriasDeCurriculoAdicionar />
          </Privado>
        }
      />
      <Route
        path="/categorias-de-curriculo/editar/:id"
        element={
          <Privado>
            <CategoriasDeCurriculoEditar />
          </Privado>
        }
      />
      <Route
        path="/relogios-de-ponto"
        element={
          <Privado>
            <RelogiosDePontoDefault />
          </Privado>
        }
      />
      <Route
        path="/relogios-de-ponto/historico"
        element={
          <Privado>
            <RelogiosDePontoHistorico />
          </Privado>
        }
      />
      <Route
        path="/relogios-de-ponto/assinar/:id"
        element={
          <Privado>
            <RelogiosDePontoAssinar />
          </Privado>
        }
      />
      <Route
        path="/fechamentos-de-folha"
        element={
          <Privado>
            <FechamentosDeFolhaDefault />
          </Privado>
        }
      />
      <Route
        path="/fechamentos-de-folha/relatorios"
        element={
          <Privado>
            <FechamentosDeFolhaRelatorios />
          </Privado>
        }
      />
      <Route
        path="/fechamentos-de-folha/historico"
        element={
          <Privado>
            <FechamentosDeFolhaHistorico />
          </Privado>
        }
      />
      <Route
        path="/fechamentos-de-folha/assinar/:id"
        element={
          <Privado>
            <FechamentosDeFolhaAssinar />
          </Privado>
        }
      />
      <Route
        path="/holerites"
        element={
          <Privado>
            <HoleritesDefault />
          </Privado>
        }
      />
      <Route
        path="/holerites/historico"
        element={
          <Privado>
            <HoleritesHistorico />
          </Privado>
        }
      />
      <Route
        path="/holerites/assinar/:id"
        element={
          <Privado>
            <HoleritesAssinar />
          </Privado>
        }
      />
      <Route
        path="/menu"
        element={
          <Privado>
            <Menu />
          </Privado>
        }
      />
      <Route
        path="/uniformes"
        element={
          <Privado>
            <UniformesDefault />
          </Privado>
        }
      />
      <Route
        path="/uniformes/adicionar"
        element={
          <Privado>
            <UniformesAdicionar />
          </Privado>
        }
      />
      <Route
        path="/uniformes/editar/:id"
        element={
          <Privado>
            <UniformesEditar />
          </Privado>
        }
      />
      <Route
        path="/epis"
        element={
          <Privado>
            <EpisDefault />
          </Privado>
        }
      />
      <Route
        path="/epis/adicionar"
        element={
          <Privado>
            <EpisAdicionar />
          </Privado>
        }
      />
      <Route
        path="/epis/editar/:id"
        element={
          <Privado>
            <EpisEditar />
          </Privado>
        }
      />
      <Route
        path="/emprestimos-epi/historico"
        element={
          <Privado>
            <EmprestimosEpiHistorico />
          </Privado>
        }
      />
      <Route
        path="/emprestimos-epi/assinar/:id"
        element={
          <Privado>
            <EmprestimosEpiAssinar />
          </Privado>
        }
      />
      <Route
        path="/emprestimos-uniforme/historico"
        element={
          <Privado>
            <EmprestimosUniformeHistorico />
          </Privado>
        }
      />
      <Route
        path="/emprestimos-uniforme/assinar/:id"
        element={
          <Privado>
            <EmprestimosUniformeAssinar />
          </Privado>
        }
      />
      <Route
        path="/avaliacoes-experiencias"
        element={
          <Privado>
            <AvaliacoesExperienciasDefault />
          </Privado>
        }
      />
      <Route
        path="/avaliacoes-desempenho"
        element={
          <Privado>
            <AvaliacoesDesempenhoDefault />
          </Privado>
        }
      />
    </Routes>
  );
};

export default Rota;
